import { useState, useEffect } from 'react'
import { Calculator as CalcIcon, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDraggable } from '@/hooks/useWidgetUtils'

// Same real sequential-operation model as before (track current value,
// pending operator, apply on "="), extended with scientific functions --
// still not eval() on a string, same reasoning as the original: eval is
// bad practice regardless of input source. Deliberately not adding
// parentheses/expression grouping -- that's a genuinely different
// architecture (a real expression parser), not a button addition, and
// wasn't explicitly asked for.
function calculate(a: number, b: number, op: string): number {
  switch (op) {
    case '+': return a + b
    case '−': return a - b
    case '×': return a * b
    case '÷': return b === 0 ? NaN : a / b
    case 'xʸ': return Math.pow(a, b)
    default: return b
  }
}

function factorial(n: number): number {
  if (n < 0 || n !== Math.floor(n)) return NaN
  let result = 1
  for (let i = 2; i <= n; i++) result *= i
  return result
}

// Unary functions apply immediately to the current display value -- no
// pending-operator interaction needed, unlike binary operators above.
function applyUnary(name: string, value: number, degrees: boolean): number {
  const toRad = (v: number) => degrees ? (v * Math.PI) / 180 : v
  switch (name) {
    case 'sin': return Math.sin(toRad(value))
    case 'cos': return Math.cos(toRad(value))
    case 'tan': return Math.tan(toRad(value))
    case '√': return value < 0 ? NaN : Math.sqrt(value)
    case 'x²': return value * value
    case 'ln': return value <= 0 ? NaN : Math.log(value)
    case 'log': return value <= 0 ? NaN : Math.log10(value)
    case '1/x': return value === 0 ? NaN : 1 / value
    case '±': return -value
    case '%': return value / 100
    case 'n!': return factorial(value)
    default: return value
  }
}

// Real calculator results get rounded to 10 significant figures before
// display -- raw JS floating point otherwise shows artifacts like
// 0.30000000000000004 or sin(30°) as 0.4999999999999999 instead of 0.5.
function formatResult(n: number): string {
  if (!isFinite(n)) return 'Error'
  const rounded = Number(n.toPrecision(10))
  return String(rounded)
}

export function CalculatorWidget({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [display, setDisplay] = useState('0')
  const [stored, setStored] = useState<number | null>(null)
  const [pendingOp, setPendingOp] = useState<string | null>(null)
  const [freshEntry, setFreshEntry] = useState(true)
  const [degrees, setDegrees] = useState(true)
  const { offset, handleProps, resetOffset } = useDraggable()

  useEffect(() => { if (open) resetOffset() }, [open, resetOffset])

  if (!open) return null

  function inputDigit(d: string) {
    if (freshEntry) {
      setDisplay(d === '.' ? '0.' : d)
      setFreshEntry(false)
    } else {
      if (d === '.' && display.includes('.')) return
      setDisplay(display === '0' && d !== '.' ? d : display + d)
    }
  }

  function inputOp(op: string) {
    const current = parseFloat(display)
    if (stored !== null && pendingOp && !freshEntry) {
      const result = calculate(stored, current, pendingOp)
      setDisplay(formatResult(result))
      setStored(result)
    } else {
      setStored(current)
    }
    setPendingOp(op)
    setFreshEntry(true)
  }

  function equals() {
    if (stored === null || pendingOp === null) return
    const current = parseFloat(display)
    const result = calculate(stored, current, pendingOp)
    setDisplay(formatResult(result))
    setStored(null)
    setPendingOp(null)
    setFreshEntry(true)
  }

  function pressUnary(name: string) {
    const current = parseFloat(display)
    const result = applyUnary(name, current, degrees)
    setDisplay(formatResult(result))
    setFreshEntry(true)
  }

  function insertConstant(value: number) {
    setDisplay(formatResult(value))
    setFreshEntry(true)
  }

  function clear() {
    setDisplay('0')
    setStored(null)
    setPendingOp(null)
    setFreshEntry(true)
  }

  const btnCls = 'h-9 rounded-lg text-xs font-medium transition-colors cursor-pointer'
  const digitCls = 'h-11 rounded-lg text-sm font-medium transition-colors cursor-pointer'
  const sciCls = cn(btnCls, 'bg-surface-elevated/60 text-text-secondary hover:bg-surface-elevated')
  const opCls = cn(digitCls, 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25')
  const numCls = cn(digitCls, 'bg-surface-elevated text-text-primary hover:bg-surface-elevated')

  return (
    <div
      className="fixed z-[70] rounded-2xl shadow-2xl backdrop-blur-xl border overflow-hidden
        bottom-4 right-4 w-[300px] max-h-[90vh] overflow-y-auto
        max-[480px]:left-4 max-[480px]:right-4 max-[480px]:bottom-4 max-[480px]:w-auto"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'rgba(255,255,255,0.12)',
        transform: `translate(${offset.x}px, ${offset.y}px)`,
      }}
    >
      <div
        className="flex items-center justify-between px-4 pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
        {...handleProps}
      >
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted">
          <CalcIcon className="h-3.5 w-3.5" />
          Scientific
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-0.5" aria-label="Close calculator">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 pb-2">
        <div className="text-right font-mono text-3xl text-text-primary tabular-nums truncate py-2">{display}</div>
      </div>

      {/* Scientific function rows */}
      <div className="px-4 pb-2 grid grid-cols-4 gap-1.5">
        <button
          onClick={() => setDegrees(d => !d)}
          className={cn(btnCls, 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25 font-semibold')}
          aria-label="Toggle degrees/radians"
        >
          {degrees ? 'DEG' : 'RAD'}
        </button>
        <button onClick={() => pressUnary('sin')} className={sciCls}>sin</button>
        <button onClick={() => pressUnary('cos')} className={sciCls}>cos</button>
        <button onClick={() => pressUnary('tan')} className={sciCls}>tan</button>

        <button onClick={() => pressUnary('log')} className={sciCls}>log</button>
        <button onClick={() => pressUnary('ln')} className={sciCls}>ln</button>
        <button onClick={() => pressUnary('√')} className={sciCls}>√</button>
        <button onClick={() => pressUnary('x²')} className={sciCls}>x²</button>

        <button onClick={() => inputOp('xʸ')} className={sciCls}>xʸ</button>
        <button onClick={() => pressUnary('1/x')} className={sciCls}>1/x</button>
        <button onClick={() => pressUnary('n!')} className={sciCls}>n!</button>
        <button onClick={() => pressUnary('%')} className={sciCls}>%</button>

        <button onClick={() => insertConstant(Math.PI)} className={sciCls}>π</button>
        <button onClick={() => insertConstant(Math.E)} className={sciCls}>e</button>
        <button onClick={() => pressUnary('±')} className={sciCls}>±</button>
        <button onClick={clear} className={cn(sciCls, 'bg-surface-elevated text-text-secondary hover:bg-surface-elevated')}>C</button>
      </div>

      {/* Standard arithmetic */}
      <div className="px-4 pb-4 grid grid-cols-4 gap-1.5">
        {['7', '8', '9'].map(d => (
          <button key={d} onClick={() => inputDigit(d)} className={numCls}>{d}</button>
        ))}
        <button onClick={() => inputOp('÷')} className={opCls}>÷</button>

        {['4', '5', '6'].map(d => (
          <button key={d} onClick={() => inputDigit(d)} className={numCls}>{d}</button>
        ))}
        <button onClick={() => inputOp('×')} className={opCls}>×</button>

        {['1', '2', '3'].map(d => (
          <button key={d} onClick={() => inputDigit(d)} className={numCls}>{d}</button>
        ))}
        <button onClick={() => inputOp('−')} className={opCls}>−</button>

        <button onClick={() => inputDigit('0')} className={cn(numCls, 'col-span-1')}>0</button>
        <button onClick={() => inputDigit('.')} className={numCls}>.</button>
        <button onClick={equals} className={cn(digitCls, 'bg-indigo-600 text-text-primary hover:bg-indigo-500')}>=</button>
        <button onClick={() => inputOp('+')} className={opCls}>+</button>
      </div>
    </div>
  )
}
